import { scaleLinear } from '@visx/scale'
import type { ContinuousStop, DiscreteStop, GradientStop, ShaderType } from 'api-validations/stitch'
import { useMemo } from 'react'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { isNil, numsFromTo } from 'utils'

import { useLocalization } from '../../localization'
import { useMapContext } from '../FieldMap/MapProvider'
import { useAsAppliedLayerOrNull, useLegendOverrides } from '../map-state-store'
import { HIGHLIGHT_COLOR, NON_HIGHLIGHT_COLOR } from '../openlayers/shader'
import { discreteStopId } from './reconcileDiscreteLegends'
import { useDefaultLegendConfig } from './useDefaultLegendConfig'
import { useReconciledDiscreteColors } from './useReconciledDiscreteColors'

export const applyOffset = (num: number, { gain, offset }: UpdateConfig) => {
  if (Number.isNaN(gain) || gain === 0) return num
  return num * gain + offset
}

type Range = {
  low: number
  high: number
}

interface ConfigurableLegendProps {
  isHighlightMode: boolean
  /**
   * The active range of the legend, which is the range from the lowest stop point
   * to the highest stop point, according to the user's current selection.
   * This MUST be smaller than or equal to the defined range, and SHOULD be
   * smaller than the editable range.
   */
  activeRange: Range
  /**
   * Editable range for the legend, which may be considerably smaller than
   * the defined range, depending on the data. This is the min and max
   * values at the extremes of the edit slider.
   */
  editableRange: Range
  /**
   * Editable number of colors in the continuous portion of the legend.
   * Each bin is a range of values that map to a single color.
   * Minimum is 3, maximum is 12
   */
  continuousBinCount: number
  /**
   * The label of the highlighted discrete value. Using the label (rather than a raw
   * blue channel value) as the key allows the same semantic value to be highlighted
   * consistently across merged streams, even when each stream encodes it at a different
   * blue channel value.
   */
  highlightLabel?: string
  /**
   * The shader type for the legend, which is either 'data' or 'boundary'.
   */
  shader: ShaderType
  /**
   * Whether the boundary file is inverted, i.e., whether the polygon is
   * considered to be inside or outside the boundary. If negated, then
   * the boundary marks out a region where the implement should not apply.
   */
  negated?: boolean
  /**
   * The adjustment factor is a correction mechanism for discrepancies between
   * real-time data from the sensor and after-the-fact data from the user.
   * Specifically, in harvest, the user can re-measure the weight of the harvest
   * with a scale, and that weight can be used to adjust the data we've already
   * stored from the sensor. The map tiles represent raw sensor data, so we need
   * to apply the adjustment factor here in the shader. In the 20|20 world, what
   * we're calling `adjustmentFactor` here is called the `k value`, and reported by
   * stitch as the `gain`. We use `gain` to mean something different here, though:
   */
  adjustmentFactor?: number
  isDiscrete: boolean
}

export type LegendOverrides = Partial<ConfigurableLegendProps>

interface NonConfigurableLegendDefinition {
  /**
   * The gradient used to derive the legend, which is an array of colors
   * and their respective stop points. This is used to derive the continuous
   * portion of the legend. Each stop is a number between 0 and 1, representing
   * the position in the gradient.
   */
  normalizedGradient: GradientStop[]
  /**
   * The colors used in the discrete portion of the legend, along with their associated
   * values. If a numeric `value` is present, it must be localized and stringified. If
   * a label is present, it is already a translated string.
   */
  discreteColors: DiscreteStop[]
  /**
   * Non-editable range for the legend, used to derive the values of a pixel,
   * i.e., to convert a pixel value (r,g,b,a) to a number.
   */
  definedRange: Range
  /**
   * The initial range of the legend, which is restored when the user resets the legend.
   * This should not change over the lifetime of the legend.
   */
  initialRange: Range
  /**
   * The unit of the legend, e.g., 'ac', 'ft', etc.
   */
  unit?: string
  /**
   * Whether the high/low values should be flipped so that the low value is on the
   * right/bottom and the high value is on the left/top.
   */
  descending: boolean
  /**
   * The name of the system (aka application), which is used to identify the legend in the UI.
   */
  name: string
}

export interface LegendState extends ConfigurableLegendProps, NonConfigurableLegendDefinition {}

/**
 * Derive a continuous legend from a gradient, step count, and bounds.
 */
export const legendFromGradient = (
  gradient: Gradient,
  stepCount: number,
  bounds: { low: number; high: number }
): Colors['continuous'] => {
  if (gradient.length === 0) {
    return []
  }
  const range = bounds.high - bounds.low

  const scaleColor = scaleLinear({
    domain: gradient.map((stop) => stop.stop),
    range: gradient.map((stop) => stop.color)
  })

  const stepIncrements = stepCount - 1 // because arrays are zero-indexed, 0 is the first step, so only stepCount - 1 remain
  const stepInterval = 1 / stepIncrements
  const labelInterval = 1 / (stepIncrements - 1) // for continuous legend, labels come between steps, so there's one fewer than

  return numsFromTo(0, 1, { interval: stepInterval }).map((stop, index) => {
    const color = scaleColor(stop)
    const result = {
      color: color,
      type: 'continuous'
    } as ContinuousStop

    const valueAt = (index: number) =>
      applyOffset(index * labelInterval, { gain: range, offset: bounds.low })

    if (index < stepCount - 1) {
      const max = valueAt(index)
      result.max = max
    }
    if (index !== 0) {
      result.min = valueAt(index - 1) // min = max at the next lowest index
    }

    return result
  })
}

/**
 * Derive a highlight legend for a layer, given its low and high bounds.
 */
const toHighlightLegend = ({ low, high }: Bounds) => {
  const highlightColors: (ContinuousStop & { label?: number })[] = [
    {
      max: low,
      color: NON_HIGHLIGHT_COLOR,
      label: low,
      type: 'continuous'
    },
    {
      max: high,
      color: HIGHLIGHT_COLOR,
      min: low,
      label: high,
      type: 'continuous'
    },
    {
      color: NON_HIGHLIGHT_COLOR,
      type: 'continuous',
      min: high
    }
  ]
  return highlightColors
}

/**
 * This is the user-facing legend for a layer, which may be
 * reflects any edits that user may have done to the default legend.
 *
 * It includes the colors and labels needed to present the legend to the user.
 * Labels have already been localized and converted to metric, if applicable.
 */
export const useUserFacingLegend = (): Colors => {
  const { map } = useMapContext()
  // Tolerates a non-as-applied selection (plan asset / plan editor): with no
  // layer there are no overrides and no default config, so the legend is empty.
  const dataLayer = useAsAppliedLayerOrNull(map.isComparison())
  const overrides = useLegendOverrides(dataLayer ?? NO_LAYER_KEY)
  const orgCode = useOrgCodeOrThrow()

  const { formatMetric } = useLocalization()

  const defaultConfig = useDefaultLegendConfig().data
  const { reconciledStops } = useReconciledDiscreteColors({
    orgCode,
    isDiscrete: defaultConfig?.isDiscrete ?? false,
    isComparison: map.isComparison()
  })

  return useMemo(() => {
    if (isNil(defaultConfig)) {
      return { continuous: [], discrete: [] }
    }
    const unit = defaultConfig.unit

    const formatContinuousStop = (stop: ContinuousStop): ContinuousItem => ({
      ...stop,
      label: isNil(stop.max)
        ? undefined
        : formatMetric({ value: stop.max, unit }, { includeUnits: false })
    })

    // Both branches produce the same { id, color, label, type } shape — the ternary
    // exists because the two source types (ReconciledDiscreteStop vs DiscreteStop)
    // carry label/value information differently.
    const discreteColors =
      reconciledStops.length > 0
        ? reconciledStops.map((entry) => ({
            id: entry.id,
            color: entry.color,
            label:
              entry.value === undefined
                ? entry.id
                : formatMetric({ value: entry.value, unit }, { includeUnits: false }),
            type: 'discrete' as const
          }))
        : defaultConfig.discreteColors.map((stop) => ({
            id: discreteStopId(stop),
            color: stop.color,
            label: stop.label ?? formatMetric({ value: stop.value, unit }, { includeUnits: false }),
            type: 'discrete' as const
          }))

    const continuousBinCount = overrides?.continuousBinCount ?? defaultConfig.continuousBinCount
    const low = overrides?.activeRange?.low ?? defaultConfig.initialRange.low
    const high = overrides?.activeRange?.high ?? defaultConfig.initialRange.high

    if (overrides?.isHighlightMode === true) {
      return defaultConfig.isDiscrete
        ? {
            values: { high, low, continuousBinCount },
            discrete: discreteColors.map((stop) => ({
              ...stop,
              color: stop.id === overrides.highlightLabel ? HIGHLIGHT_COLOR : NON_HIGHLIGHT_COLOR
            })),
            continuous: []
          }
        : {
            values: { high, low, continuousBinCount },
            discrete: [],
            continuous: toHighlightLegend({ low, high }).map(formatContinuousStop)
          }
    }

    return {
      discrete: discreteColors,
      values: { high, low, continuousBinCount },
      continuous: legendFromGradient(defaultConfig.normalizedGradient, continuousBinCount, {
        low,
        high
      }).map(formatContinuousStop)
    }
  }, [overrides, defaultConfig, formatMetric, reconciledStops])
}

type UpdateConfig = {
  gain: number
  offset: number
}

type Bounds = {
  high: number
  low: number
}

type Gradient = {
  color: string
  stop: number
}[]

type Colors = {
  discrete: DiscreteItem[]
  continuous: ContinuousItem[]
}

/** Override-map key used when the pane has no as-applied layer; never written to. */
const NO_LAYER_KEY = { streamId: '', applicationId: '', layerId: '' }

export type ContinuousItem = {
  type: 'continuous'
  max?: number
  min?: number
  color: string
  label?: string
}

type DiscreteItem = {
  type: 'discrete'
  /**
   * The identity key for this stop — `stop.label` if present, else `String(stop.value)`.
   * Used for highlight matching and bin color matching. Not necessarily display-ready;
   * use `label` for display.
   */
  id: string
  color: string
  label: string
}

/* ---------------------------------- tests --------------------------------- */

if (import.meta.vitest) {
  const { test, describe, expect } = import.meta.vitest

  describe('gain and offset', () => {
    test('when applying an offset to a value, the results should be correct', () => {
      const prev = { low: 0, high: 40 }
      const expected = { low: 35, high: 45 }

      const offset = (val: number) => applyOffset(val, { offset: 35, gain: 0.25 })

      expect(offset(prev.low)).toBe(expected.low)
      expect(offset(prev.high)).toBe(expected.high)
    })

    test('applying an offset with 0 gain should not change the number', () => {
      expect(applyOffset(12.4, { gain: 0, offset: 430 })).toBe(12.4)
    })
  })

  describe('Creating and editing legend color arrays', () => {
    test('given a gradient, plus a gain and offset, legendFromGradient should generate a correct legend', () => {
      const gradient = [
        {
          color: '#ff462d', // equals rgb(255, 70, 45)
          stop: 0,
          type: 'continuous'
        },
        {
          color: '#ffff00',
          stop: 0.5,
          type: 'continuous'
        },
        {
          color: '#00c326', // equals rgb(0, 195, 38)
          stop: 1,
          type: 'continuous'
        }
      ]

      const actual = legendFromGradient(gradient, 10, {
        low: -430,
        high: 1089
      })

      expect(actual.length).toBe(10)
      expect(actual[0].color).toBe('rgb(255, 70, 45)')
      expect(actual[0].max).toBe(-430)
      expect(actual[9].color).toBe('rgb(0, 195, 38)')
      expect(actual[8].max).toBe(1089)
      expect(actual[9].label).toBe(undefined)
      expect(actual).toMatchInlineSnapshot(`
        [
          {
            "color": "rgb(255, 70, 45)",
            "max": -430,
            "type": "continuous",
          },
          {
            "color": "rgb(255, 111, 35)",
            "max": -240.125,
            "min": -430,
            "type": "continuous",
          },
          {
            "color": "rgb(255, 152, 25)",
            "max": -50.25,
            "min": -240.125,
            "type": "continuous",
          },
          {
            "color": "rgb(255, 193, 15)",
            "max": 139.625,
            "min": -50.25,
            "type": "continuous",
          },
          {
            "color": "rgb(255, 234, 5)",
            "max": 329.5,
            "min": 139.625,
            "type": "continuous",
          },
          {
            "color": "rgb(227, 248, 4)",
            "max": 519.375,
            "min": 329.5,
            "type": "continuous",
          },
          {
            "color": "rgb(170, 235, 13)",
            "max": 709.25,
            "min": 519.375,
            "type": "continuous",
          },
          {
            "color": "rgb(113, 222, 21)",
            "max": 899.125,
            "min": 709.25,
            "type": "continuous",
          },
          {
            "color": "rgb(57, 208, 30)",
            "max": 1089,
            "min": 899.125,
            "type": "continuous",
          },
          {
            "color": "rgb(0, 195, 38)",
            "min": 1089,
            "type": "continuous",
          },
        ]
      `)
    })
  })
}
